import { memo, useMemo } from 'react';
import { Avatar, Badge, DataTable, StatusMessage } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Users } from 'lucide-react';

import { formatRelativeTime } from '../../utils/formatRelativeTime';
import { getAvatarColorClass } from '../../utils/avatarColor';
import { initialsOf } from '../../utils/groupPaths';
import StatusBadge from '../shared/StatusBadge';

const ACTIONS_COLUMN_CLASS = 'w-12 text-right';
// StatusMessage brings its own vertical padding.
const TABLE_CLASS_NAMES = Object.freeze({ emptyCell: 'py-0' });

/**
 * Members of a group: Member, Job title, Last active, MFA, Status and a trailing actions slot
 * (`renderRowActions`) for per-row actions such as removal. Removed/suspended members stay in the
 * table with their status badge.
 *
 * @param {object} props
 * @param {object[]} props.members UI members (`mapApiGroupMemberToUi`).
 * @param {boolean} [props.loading]
 * @param {(member: object) => void} [props.onRowClick] Opens the member (their detail page).
 * @param {(member: object) => import('react').ReactNode} [props.renderRowActions] Stable callback.
 */
const GroupMembersTable = ({ members, loading = false, onRowClick, renderRowActions }) => {
  const { t, i18n } = useTranslation('people-access');
  const locale = i18n?.language;

  const emptyMessage = useMemo(
    () => (
      <StatusMessage
        icon={Users}
        title={t('groups.members.empty')}
        subtitle={t('groups.members.emptyHint')}
      />
    ),
    [t],
  );

  const columns = useMemo(() => {
    const base = [
      {
        key: 'name',
        label: t('groups.members.member'),
        render: (_, member) => (
          <div className="flex items-center gap-3">
            <Avatar
              size="lg"
              initials={initialsOf(member.name)}
              alt={member.name}
              className={`${getAvatarColorClass(member.name)} text-xs`}
            />
            <div className="flex min-w-0 flex-col">
              <span className="truncate text-sm font-medium text-content">{member.name}</span>
              <span className="truncate text-xs text-content-muted">{member.email}</span>
            </div>
          </div>
        ),
      },
      {
        key: 'jobTitle',
        label: t('groups.members.jobTitle'),
        render: (value) => <span className="text-content-secondary">{value ?? '—'}</span>,
      },
      {
        key: 'lastActiveAt',
        label: t('groups.members.lastActive'),
        render: (value) => (
          <span className="text-content-secondary">
            {formatRelativeTime(value, locale) ?? t('groups.members.never')}
          </span>
        ),
      },
      {
        key: 'mfaEnabled',
        label: t('groups.members.mfa'),
        render: (value) => (
          <Badge variant={value ? 'success' : 'warning'}>
            {t(value ? 'groups.members.mfaOn' : 'groups.members.mfaOff')}
          </Badge>
        ),
      },
      {
        key: 'status',
        label: t('groups.members.status'),
        render: (value) => <StatusBadge status={value} />,
      },
    ];

    return renderRowActions
      ? [
          ...base,
          {
            key: 'actions',
            label: '',
            headerClassName: ACTIONS_COLUMN_CLASS,
            cellClassName: ACTIONS_COLUMN_CLASS,
            render: (_, member) => renderRowActions(member),
          },
        ]
      : base;
  }, [t, locale, renderRowActions]);

  return (
    <div role="region" tabIndex={0} className="scrollbar-aimsos-auto-sm overflow-x-auto rounded-lg border border-edge focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50" aria-label={t('groups.members.tableAria')}>
      <DataTable
        columns={columns}
        data={members}
        loading={loading}
        onRowClick={onRowClick}
        getRowId={getMemberId}
        classNames={TABLE_CLASS_NAMES}
        emptyMessage={emptyMessage}
      />
    </div>
  );
};

function getMemberId(member) {
  return member.id;
}

export default memo(GroupMembersTable);
